import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  CATEGORY_COLORS, CATEGORY_LABELS, EventMapPoint, MapPointFilters, filterMapPoints,
} from '@radevents/shared';
import LocationRadiusMap, { LatLng, countInRadius, distanceKm } from './LocationRadiusMap';
import { loadPlaces, searchPlaces, Place } from '../data/places';
import eventService from '../services/eventService';
import { umamiTrack } from '../services/analytics';

/**
 * Ort & Umkreis auswählen — auf dem Desktop als Modal, auf dem Phone als Vollbild-Screen.
 *
 * Beides arbeitet auf einem **Draft-State**: Ortssuche, Karten-Klick, Slider und die Filter
 * (Kategorie, Datum, Distanz) ändern nur die Vorschau; erst „Fertig" bzw. der CTA übernimmt sie
 * in den Filter. „Abbrechen" verwirft. So bleibt die Ergebnisliste ruhig, während man sucht.
 *
 * Die drei Filter stehen hier, weil der Umkreis ohne sie kaum zu beurteilen ist: „50 Events im
 * Umkreis" hilft niemandem, der nur Gravel über 150 km fahren will. Sie schränken deshalb Pins
 * **und** Trefferzahl sofort ein und wandern beim Übernehmen mit in den Filter der Seite.
 *
 * Der **Umkreis lässt sich ausschalten**: dann verschwindet der Kreis, die Karte bleibt stehen,
 * wo man sie hingeschoben hat, und die Zahl zählt alle Events der gesetzten Filter. Die Übernahme
 * entfernt in diesem Fall den Entfernungsfilter aus der Liste — sonst versprächen Zahl und Knopf
 * etwas, das die Seite danach nicht zeigt.
 *
 * In der Terminliste lassen sich die **permanenten Events als zweite Ebene** einblenden
 * (`permanentPoints`). Sie zählen bewusst nicht zur Trefferzahl: „Fertig" filtert die Terminliste,
 * und dort stehen sie nicht. Stattdessen nennt eine eigene Zeile, wie viele im Umkreis liegen, und
 * führt mit demselben Ort und Umkreis in die Liste „Permanent".
 */

const BERRY = '#922C57';
const RADIUS_MIN = 10;
const RADIUS_MAX = 300;
const RADIUS_STEP = 10;
/** Fallback-Zentrum, wenn weder Filter noch Standort etwas hergeben (geografische Mitte DE). */
const DEFAULT_CENTER: LatLng = { lat: 51.16, lng: 10.45 };
/** Merker für den Schalter „Permanente Events einblenden" — je Browser, nicht je Konto. */
const SHOW_PERMANENT_KEY = 'radevents.karte.permanente';
/** Abstand der schwebenden Steuerung zum oberen Kartenrand (Vollbild-Variante). */
const FLOATING_TOP = 12;
/**
 * Größe des Desktop-Modals. Es nutzt den Bildschirm jetzt weitgehend aus: auf einem
 * Standard-Monitor stand die Karte vorher in einem Briefmarken-Format, obwohl links und rechts
 * Platz blieb — und eine Umkreis-Karte lebt davon, dass man Nachbarorte erkennt.
 *
 * Die Höhe folgt dem Fenster, bleibt aber nach unten bei 520 px stehen: darunter passen
 * Ortssuche, Umkreis und die drei Filter nicht mehr nebeneinander in die Seitenspalte.
 */
const MODAL_MAX_WIDTH = 1180;
const MODAL_BODY_HEIGHT = 'min(720px, max(520px, 78vh))';

/** Dieselben Stufen wie in der Filter-Sidebar — zwei Zahlenfelder wären auf der Karte zu fummelig. */
const DIST_PRESETS: { label: string; min: string; max: string }[] = [
  { label: '< 50 km', min: '', max: '50' },
  { label: '50–100 km', min: '50', max: '100' },
  { label: '100–150 km', min: '100', max: '150' },
  { label: '> 150 km', min: '150', max: '' },
];

/** Die Filter, die der Picker selbst anbietet — Werte wie im Filter der Seite (leer = aus). */
export interface PickedFilters {
  category: string;
  date: string;
  minKm: string;
  maxKm: string;
}

/** Der gewählte Ort — das, was die Liste als Umkreis-Mittelpunkt behält. */
export interface PickedLocation {
  name: string;
  lat: number;
  lng: number;
  /** `null` = Umkreis ausgeschaltet: die Liste filtert dann nicht nach Entfernung. */
  radiusKm: number | null;
}

/** Was „Fertig" zurückgibt: der Ort plus die im Dialog gesetzten Filter. */
export interface PickedSelection extends PickedLocation {
  filters: PickedFilters;
}

interface LocationPickerProps {
  variant: 'modal' | 'fullscreen';
  onClose: () => void;
  onApply: (picked: PickedSelection) => void;
  /** Startwerte aus dem aktuellen Filter. */
  initialCenter?: LatLng | null;
  initialRadiusKm?: number | null;
  initialName?: string;
  initialFilters?: PickedFilters;
  points: EventMapPoint[];
  /** Ids der eigenen Favoriten — sie werden auf der Karte mit einem Herz hervorgehoben. */
  favoriteIds?: Set<number>;
  /**
   * Filter, die der Picker nicht anbietet (Land, Region) — sie schränken Pins und Trefferzahl
   * trotzdem ein, sonst zählte die Karte Events mit, die die Liste danach gar nicht zeigt.
   */
  lockedFilters?: MapPointFilters;
  /**
   * Aktive Eventarten. Sie hängen an der Kategorie: wird die hier geändert, setzt die Übernahme
   * sie zurück — dann dürfen sie auch die Vorschau nicht mehr einschränken.
   */
  eventTypes?: string[];
  /** Kartenpunkte werden noch geladen — solange gibt es keine belastbare Trefferzahl. */
  pointsLoading?: boolean;
  /**
   * Permanente Events als zuschaltbare zweite Ebene (nur in der Terminliste). Fehlt die Prop,
   * gibt es auch den Schalter nicht.
   */
  permanentPoints?: EventMapPoint[];
  /** „Anzeigen" an der Zeile der permanenten Events: Ort, Umkreis und Filter in die Liste „Permanent". */
  onShowPermanent?: (picked: PickedSelection) => void;
  /** Startwert des Schalters statt des gemerkten (Design-Lab). */
  initialShowPermanent?: boolean;
}

function readShowPermanent(): boolean {
  try { return window.localStorage.getItem(SHOW_PERMANENT_KEY) === '1'; } catch { return false; }
}

function writeShowPermanent(on: boolean) {
  try { window.localStorage.setItem(SHOW_PERMANENT_KEY, on ? '1' : '0'); } catch { /* ohne Speicher gilt der Schalter nur für diese Öffnung */ }
}

const EMPTY_FILTERS: PickedFilters = { category: '', date: '', minKm: '', maxKm: '' };

const mono: React.CSSProperties = { fontFamily: "'Space Mono',monospace", letterSpacing: '0.12em', color: '#9A9494', textTransform: 'uppercase' };
const body = "'Hanken Grotesk',sans-serif";

const PinIcon = ({ size = 16, color = BERRY }: { size?: number; color?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 21s7-6.2 7-11a7 7 0 1 0-14 0c0 4.8 7 11 7 11z" /><circle cx="12" cy="10" r="2.6" />
  </svg>
);

/** Kleiner hohler Pin wie auf der Karte — erklärt Schalter und Zählzeile ohne Legende. */
const PermanentMark = () => (
  <svg width={11} height={14} viewBox="-1.5 -1.5 27 35" aria-hidden style={{ flexShrink: 0 }}>
    <path d="M12 0C5.4 0 0 5.4 0 12c0 8.4 12 20 12 20s12-11.6 12-20C24 5.4 18.6 0 12 0z"
      fill="#fff" stroke={BERRY} strokeWidth={2.6} strokeDasharray="4.2 2.6" />
    <circle cx="12" cy="12" r="4.2" fill={BERRY} />
  </svg>
);

export default function LocationPicker({
  variant, onClose, onApply, initialCenter, initialRadiusKm, initialName,
  initialFilters = EMPTY_FILTERS, lockedFilters, eventTypes, points, favoriteIds, pointsLoading = false,
  permanentPoints, onShowPermanent, initialShowPermanent,
}: LocationPickerProps) {
  const fullscreen = variant === 'fullscreen';
  const [center, setCenter] = useState<LatLng>(initialCenter ?? DEFAULT_CENTER);
  const [radius, setRadius] = useState<number>(initialRadiusKm ?? 100);
  /**
   * Umkreis an/aus. Ausgeschaltet verschwindet der Kreis, die Karte bleibt stehen, wo man sie
   * hingeschoben hat, und die Zahl zählt alle Events der gesetzten Filter — so lässt sich die
   * Karte erkunden, ohne dass der Radius den Ausschnitt immer wieder zurückholt.
   */
  const [radiusOn, setRadiusOn] = useState(true);
  const [name, setName] = useState<string>(initialName ?? '');
  const [filters, setFilters] = useState<PickedFilters>(initialFilters);
  const [showPermanent, setShowPermanent] = useState(() => initialShowPermanent ?? readShowPermanent());
  const toggleShowPermanent = () => setShowPermanent(v => { writeShowPermanent(!v); return !v; });
  // Auf dem Phone liegen die Filter über der Karte und würden sie zuklappen — dort also erst
  // auf Antippen. Im Modal ist links Platz, dort stehen sie offen.
  const [filtersOpen, setFiltersOpen] = useState(!fullscreen);
  const [query, setQuery] = useState('');
  const [places, setPlaces] = useState<Place[] | null>(null);
  const [locating, setLocating] = useState(false);
  const [locateError, setLocateError] = useState<string | null>(null);
  const searchWrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => { loadPlaces().then(setPlaces).catch(() => {}); }, []);

  // Mengengerüst für die Kachellast bei OpenStreetMap: jede Öffnung zieht einen Kachelsatz.
  // Zusammen mit `karte-kachelfehler` ergibt das die Frühwarnung, ob wir an deren Grenzen stoßen.
  useEffect(() => { umamiTrack('karte-geoeffnet', { variante: variant }); }, [variant]);

  // Escape schließt — bei einem Vollbild-Overlay ohne sichtbaren Rahmen sonst nur über „Abbrechen".
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const suggestions = useMemo(
    () => (places && query.trim().length >= 2 ? searchPlaces(places, query.trim(), 6) : []),
    [places, query]
  );

  /**
   * Postleitzahlen stehen nicht im Ortsverzeichnis (GeoNames kennt nur Ortsnamen) — das Feld
   * verspricht aber „Ort oder PLZ". Eine reine Ziffernfolge wird deshalb serverseitig aufgelöst:
   * 5 Ziffern = Deutschland, 4 = Österreich oder Schweiz.
   *
   * Ein Status statt mehrerer Flags: mit getrennten `loading`/`hit`-Flags stünde vor dem ersten
   * Effektlauf kurz „nicht gefunden" da, obwohl noch gar nicht gesucht wurde. Und `incomplete`
   * ist der eigentlich wichtige Zustand — beim Tippen von „309" gäbe es sonst überhaupt keine
   * Rückmeldung, das Feld wirkt schlicht kaputt.
   */
  type PlzStatus =
    | { kind: 'none' }
    | { kind: 'incomplete'; missing: number }
    | { kind: 'loading' }
    | { kind: 'hit'; plz: string; lat: number; lng: number }
    | { kind: 'miss' };
  const [plz, setPlz] = useState<PlzStatus>({ kind: 'none' });
  useEffect(() => {
    const q = query.trim();
    if (!/^\d+$/.test(q)) { setPlz({ kind: 'none' }); return; }
    // 1–3 Ziffern können nur eine angefangene deutsche PLZ sein (AT/CH haben vier).
    if (q.length < 4) { setPlz({ kind: 'incomplete', missing: 5 - q.length }); return; }
    if (q.length > 5) { setPlz({ kind: 'miss' }); return; }
    let cancelled = false;
    setPlz({ kind: 'loading' });
    const timer = setTimeout(async () => {
      for (const country of q.length === 5 ? ['DE'] : ['AT', 'CH']) {
        const found = await eventService.geocodePlz(q, country);
        if (cancelled) return;
        if (found) { setPlz({ kind: 'hit', plz: q, lat: found.lat, lng: found.lng }); return; }
      }
      if (!cancelled) setPlz({ kind: 'miss' });
    }, 400);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [query]);

  /**
   * Die Punkte, die nach allen Filtern übrig bleiben — Grundlage für Pins *und* Trefferzahl.
   *
   * Die Eventarten gelten nur, solange die Kategorie unverändert ist: ein Wechsel setzt sie beim
   * Übernehmen zurück (wie im Filter der Seite), also dürfen sie die Vorschau dann nicht mehr
   * einschränken — sonst zählte die Karte gegen einen Filter, den es gleich nicht mehr gibt.
   */
  const pointFilters = useMemo<MapPointFilters>(() => ({
    ...lockedFilters,
    category: filters.category || null,
    date: filters.date || null,
    minKm: filters.minKm ? Number(filters.minKm) : null,
    maxKm: filters.maxKm ? Number(filters.maxKm) : null,
    eventTypes: filters.category === initialFilters.category ? eventTypes : [],
  }), [lockedFilters, filters, eventTypes, initialFilters.category]);
  const visiblePoints = useMemo(() => filterMapPoints(points, pointFilters), [points, pointFilters]);

  // Die permanente Ebene mit denselben Filtern — Datum und „bis" greifen bei ihr nicht
  // (`matchesMapPointFilters` überspringt sie für permanente Punkte).
  const visiblePermanent = useMemo(
    () => (permanentPoints && showPermanent ? filterMapPoints(permanentPoints, pointFilters) : []),
    [permanentPoints, showPermanent, pointFilters]
  );

  // Ohne Umkreis zählt, was die Filter übrig lassen — dieselbe Menge, die die Liste danach zeigt.
  const hits = useMemo(
    () => (radiusOn ? countInRadius(visiblePoints, center, radius) : visiblePoints.length),
    [visiblePoints, center, radius, radiusOn]
  );
  const permanentHits = useMemo(
    () => (radiusOn ? countInRadius(visiblePermanent, center, radius) : 0),
    [visiblePermanent, center, radius, radiusOn]
  );

  /**
   * Wie viel der Karte die schwebende Steuerung oben verdeckt. Der Kreis wird darunter zentriert,
   * sonst säße er halb hinter Suchfeld und Umkreis-Karte (die Karte reicht ja bis unter den Header).
   *
   * Zwei Zustände zählen bewusst **nicht** mit, obwohl sie die Spalte höher machen: aufgeklappte
   * Filter (die dürfen die Karte verdecken) und die Vorschlagsliste beim Tippen. Beide sind
   * vorübergehend — würde der Ausschnitt ihnen folgen, zoomte die Karte bei jedem Tastendruck.
   */
  const floatingRef = useRef<HTMLDivElement>(null);
  const [mapInsetTop, setMapInsetTop] = useState(0);
  const suggestOpen = suggestions.length > 0 || plz.kind !== 'none';
  useLayoutEffect(() => {
    if (!fullscreen || filtersOpen || suggestOpen) return;
    const el = floatingRef.current;
    if (!el) return;
    const measure = () => setMapInsetTop(Math.round(el.getBoundingClientRect().height) + FLOATING_TOP);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [fullscreen, filtersOpen, suggestOpen]);

  const pickPlace = (p: Place) => {
    setCenter({ lat: p.lat, lng: p.lng });
    setName(p.name);
    setQuery('');
  };

  const pickPlz = (hit: { plz: string; lat: number; lng: number }) => {
    setCenter({ lat: hit.lat, lng: hit.lng });
    setName(hit.plz);
    setQuery('');
  };

  /** Enter im Suchfeld nimmt den naheliegendsten Vorschlag — die gefundene PLZ, sonst den ersten Ort. */
  const takeFirstSuggestion = () => {
    if (plz.kind === 'hit') pickPlz(plz);
    else if (suggestions.length > 0) pickPlace(suggestions[0]);
  };

  /**
   * Karten-Klick benennen: der nächstgelegene bekannte Ort, sofern er noch plausibel „hier" ist.
   * Ohne Namen stünde in der Filterzeile später nur eine Koordinate.
   */
  const pickCoord = (coord: LatLng) => {
    setCenter(coord);
    // Wer die Karte antippt, hat den Standort-Hinweis beantwortet — er darf weg.
    setLocateError(null);
    let best: { name: string; d: number } | null = null;
    for (const p of places ?? []) {
      const d = distanceKm(coord, { lat: p.lat, lng: p.lng });
      if (!best || d < best.d) best = { name: p.name, d };
    }
    setName(best && best.d <= 25 ? best.name : 'Gewählter Punkt');
  };

  /**
   * Eigenen Standort übernehmen.
   *
   * Jeder Ausgang bekommt eine sichtbare Antwort. Vorher verschwand jeder Fehler still:
   * verweigerte Freigabe, Zeitüberschreitung und fehlender Empfang sahen alle aus wie ein
   * toter Knopf — und genau so wurde es gemeldet. Der häufigste Fall ist die einmal
   * abgelehnte Freigabe, die der Browser danach ohne Rückfrage abweist; ohne Hinweis hat
   * niemand eine Chance, das zu verstehen.
   *
   * `maximumAge` lässt eine wenige Minuten alte Ortung gelten und `timeout` ist großzügiger:
   * auf dem Handy braucht eine frische Bestimmung regelmäßig länger als die alten 8 s, und
   * für einen Umkreis von 10–300 km spielt es keine Rolle, ob die Position auf 20 m oder
   * 2 km genau ist.
   */
  const useOwnLocation = () => {
    if (!navigator.geolocation || !window.isSecureContext) {
      setLocateError('Dieser Browser gibt den Standort nicht heraus.');
      return;
    }
    setLocating(true);
    setLocateError(null);
    navigator.geolocation.getCurrentPosition(
      pos => {
        setLocating(false);
        pickCoord({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        umamiTrack('standort-button', { ergebnis: 'ok' });
      },
      err => {
        setLocating(false);
        const denied = err.code === err.PERMISSION_DENIED;
        setLocateError(
          denied ? 'Standortfreigabe ist blockiert. Sie lässt sich in den Browser-Einstellungen für diese Seite wieder erlauben.'
            : err.code === err.TIMEOUT ? 'Standort dauert zu lange — bitte noch einmal antippen.'
            : 'Standort ist gerade nicht verfügbar.'
        );
        // Ohne diese Zahlen lässt sich nicht sagen, ob der Knopf tatsächlich klemmt oder
        // ob schlicht die Freigabe fehlt — genau diese Frage hat den Fehlerbericht gekostet.
        umamiTrack('standort-button', {
          ergebnis: denied ? 'verweigert' : err.code === err.TIMEOUT ? 'zeitueberschreitung' : 'nicht-verfuegbar',
        });
      },
      { timeout: 20000, maximumAge: 300000 }
    );
  };

  const selection = (): PickedSelection => ({
    name: name || 'Gewählter Punkt', lat: center.lat, lng: center.lng,
    radiusKm: radiusOn ? radius : null, filters,
  });
  const apply = () => onApply(selection());

  const setFilter = (patch: Partial<PickedFilters>) => setFilters(f => ({ ...f, ...patch }));
  const isDistActive = (p: { min: string; max: string }) =>
    filters.minKm === p.min && filters.maxKm === p.max && (p.min !== '' || p.max !== '');
  const toggleDist = (p: { min: string; max: string }) =>
    setFilter(isDistActive(p) ? { minKm: '', maxKm: '' } : { minKm: p.min, maxKm: p.max });

  const filterCount = [filters.category, filters.date, filters.minKm || filters.maxKm].filter(Boolean).length;
  const filterSummary = filterCount === 0 ? 'Alle Events' : [
    filters.category ? CATEGORY_LABELS[filters.category] ?? filters.category : null,
    filters.date ? `ab ${filters.date.split('-').reverse().join('.')}` : null,
    DIST_PRESETS.find(p => isDistActive(p))?.label ?? null,
  ].filter(Boolean).join(' · ');
  // Der Schalter ist kein Filter (Zurücksetzen lässt ihn stehen), steht aber in der Zusammenfassung,
  // damit die hohlen Pins auf dem Phone bei zugeklappten Filtern nicht unerklärt bleiben.
  const summaryText = permanentPoints && showPermanent ? `${filterSummary} · mit permanenten` : filterSummary;

  // Solange die Kartenpunkte fehlen, ist „0 Treffer" keine Aussage, sondern eine Lüge — deshalb
  // sagen Trefferzeile und CTA in dieser Zeit nur, dass gezählt wird.
  const hitText = pointsLoading ? 'Events werden gezählt…'
    : radiusOn ? (hits === 1 ? '1 Event im Umkreis' : `${hits} Events im Umkreis`)
    : (hits === 1 ? '1 Event (ohne Umkreis)' : `${hits} Events (ohne Umkreis)`);
  const ctaLabel = pointsLoading ? 'Übernehmen'
    : hits === 0 ? (radiusOn ? 'Umkreis vergrößern' : 'Filter lockern')
    : hits === 1 ? '1 Event anzeigen'
    : `${hits} Events anzeigen`;

  const radiusSlider = (
    <input
      type="range" min={RADIUS_MIN} max={RADIUS_MAX} step={RADIUS_STEP} value={radius}
      onChange={e => setRadius(Number(e.target.value))}
      disabled={!radiusOn}
      aria-label="Umkreis in Kilometern"
      // Ausgegraut statt ausgeblendet: der Regler behält seinen Platz, die Karte springt beim
      // Umschalten also nicht.
      style={{ width: '100%', accentColor: BERRY, cursor: radiusOn ? 'pointer' : 'default', opacity: radiusOn ? 1 : 0.4 }}
    />
  );

  /**
   * Schalter „Umkreis". Aus heißt: kein Kreis, kein Zurückspringen des Ausschnitts, und die
   * Übernahme entfernt den Entfernungsfilter aus der Liste — die Karte ist dann nur noch
   * Übersicht, keine Auswahl.
   */
  const switchButton = (on: boolean, onToggle: () => void, label: string) => (
    <button
      type="button" role="switch" aria-checked={on} aria-label={label}
      onClick={onToggle}
      style={{
        position: 'relative', width: 38, height: 22, flexShrink: 0, padding: 0, borderRadius: 999,
        border: 'none', cursor: 'pointer', background: on ? BERRY : '#D8D3D3',
      }}
    >
      <span style={{
        position: 'absolute', top: 3, left: on ? 19 : 3, width: 16, height: 16, borderRadius: '50%',
        background: '#fff', boxShadow: '0 1px 3px rgba(38,38,38,0.35)', transition: 'left 0.15s ease',
      }} />
    </button>
  );
  const radiusToggle = switchButton(radiusOn, () => setRadiusOn(v => !v), 'Umkreis begrenzen');

  /** Rechts in der Umkreis-Zeile: Weite und Schalter — in beiden Varianten dieselbe Anordnung. */
  const radiusValue = (
    <span style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
      <span style={{ fontFamily: "'Space Mono',monospace", fontWeight: 700, fontSize: 14, color: radiusOn ? '#2C2C2C' : '#9A9494' }}>
        {radiusOn ? `${radius} km` : 'aus'}
      </span>
      {radiusToggle}
    </span>
  );

  /** Kategorie, Datum, Distanz — dieselben Stufen und Beschriftungen wie im Filter der Seite. */
  const filterControls = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div>
        <div style={{ ...mono, fontSize: 10, marginBottom: 7 }}>Kategorie</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 6 }}>
          {Object.entries(CATEGORY_LABELS).map(([key, label]) => {
            const active = filters.category === key;
            return (
              <button
                key={key} type="button"
                onClick={() => setFilter({ category: active ? '' : key })}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '9px 4px',
                  borderRadius: 9, cursor: 'pointer', fontFamily: body, fontWeight: 600, fontSize: 13,
                  background: active ? '#2C2C2C' : '#F5F3F3', color: active ? '#fff' : '#4A4A4A',
                  border: `1px solid ${active ? '#2C2C2C' : '#E5E1E1'}`, minWidth: 0,
                }}
              >
                <span style={{
                  width: 8, height: 8, borderRadius: 2.5, transform: 'rotate(45deg)', flexShrink: 0,
                  background: active ? '#fff' : (CATEGORY_COLORS[key] || BERRY),
                }} />
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
              </button>
            );
          })}
        </div>
      </div>
      <div>
        <div style={{ ...mono, fontSize: 10, marginBottom: 7 }}>Ab Datum</div>
        <input
          type="date" value={filters.date} onChange={e => setFilter({ date: e.target.value })}
          aria-label="Events ab Datum"
          style={{
            width: '100%', boxSizing: 'border-box', background: '#F5F3F3', border: '1px solid #E5E1E1',
            borderRadius: 9, padding: '9px 11px', fontFamily: body, fontWeight: 600, fontSize: 13.5, color: '#2C2C2C',
          }}
        />
      </div>
      <div>
        <div style={{ ...mono, fontSize: 10, marginBottom: 7 }}>Distanz</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {DIST_PRESETS.map(p => {
            const active = isDistActive(p);
            return (
              <button
                key={p.label} type="button" onClick={() => toggleDist(p)}
                style={{
                  cursor: 'pointer', padding: '7px 11px', borderRadius: 8, fontFamily: body, fontWeight: 600, fontSize: 12.5,
                  background: active ? '#2C2C2C' : '#F5F3F3', color: active ? '#fff' : '#2C2C2C',
                  border: `1px solid ${active ? '#2C2C2C' : '#E5E1E1'}`,
                }}
              >
                {p.label}
              </button>
            );
          })}
        </div>
      </div>
      {permanentPoints && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, paddingTop: 2 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: body, fontWeight: 600, fontSize: 13.5, color: '#2C2C2C' }}>
            <PermanentMark />
            Permanente Events einblenden
          </span>
          {switchButton(showPermanent, toggleShowPermanent, 'Permanente Events einblenden')}
        </div>
      )}
      {filterCount > 0 && (
        <button
          type="button" onClick={() => setFilters(EMPTY_FILTERS)}
          style={{ alignSelf: 'flex-start', background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: body, fontWeight: 600, fontSize: 12.5, color: BERRY }}
        >
          Filter zurücksetzen
        </button>
      )}
    </div>
  );

  const hitLine = (fontSize: number, dot: number) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: body, fontWeight: 700, fontSize, color: '#2C2C2C' }}>
        <span style={{ width: dot, height: dot, background: BERRY, transform: 'rotate(45deg)', borderRadius: 2, flexShrink: 0 }} />
        {hitText}
      </div>
      {permanentLine(fontSize - 1)}
    </div>
  );

  /**
   * „+ 3 permanente Events im Umkreis" — nur mit eingeblendeter Ebene und aktivem Umkreis. Ohne
   * Umkreis gäbe es kein „in der Nähe", und die Zahl aller permanenten Events sagt dort nichts.
   */
  const permanentLine = (fontSize: number) => {
    if (!permanentPoints || !showPermanent || !radiusOn || permanentHits === 0) return null;
    return (
      // Text und „Anzeigen" fließen als eine Zeile: bricht sie in der schmalen Spalte um, steht der
      // Knopf eingerückt unter dem Text statt unter dem Symbol.
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontFamily: body, fontSize, lineHeight: 1.4, color: '#4A4A4A' }}>
        <span style={{ display: 'flex', paddingTop: 2 }}><PermanentMark /></span>
        <span>
          + {permanentHits === 1 ? '1 permanentes Event' : `${permanentHits} permanente Events`} im Umkreis
          {onShowPermanent && (
            <>
              {' '}
              <button
                type="button" onClick={() => onShowPermanent(selection())}
                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: body, fontWeight: 700, fontSize, color: BERRY }}
              >
                Anzeigen
              </button>
            </>
          )}
        </span>
      </div>
    );
  };

  /** `floating` = das Feld liegt auf der Karte und braucht deshalb einen tragenden Schatten. */
  const searchField = (compact: boolean, floating = false) => (
    <div ref={searchWrapRef} style={{ position: 'relative' }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        background: compact ? '#F5F3F3' : '#fff', border: '1px solid #E5E1E1',
        borderRadius: compact ? 10 : 999, padding: compact ? '10px 12px' : '12px 16px', boxSizing: 'border-box',
        boxShadow: floating ? '0 6px 20px rgba(38,38,38,0.18)' : 'none',
      }}>
        <span style={{ color: '#9A9494', fontSize: 16, lineHeight: 1 }}>⌕</span>
        <input
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); takeFirstSuggestion(); } }}
          placeholder="Ort oder PLZ"
          aria-label="Ort oder PLZ suchen"
          style={{ flex: 1, minWidth: 0, border: 'none', background: 'transparent', outline: 'none', fontFamily: body, fontSize: compact ? 14 : 15, color: '#2C2C2C' }}
        />
        {query && (
          <button type="button" onClick={() => setQuery('')} aria-label="Ortssuche leeren"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9A9494', fontSize: 14, padding: 0, lineHeight: 1 }}>✕</button>
        )}
      </div>
      {(suggestions.length > 0 || plz.kind !== 'none') && (
        <div style={{
          // Über Leaflets Panes: die reichen bis z-index 800 und `.leaflet-container` bildet
          // keinen eigenen Stacking-Context — mit einem kleinen Wert verschwindet die Liste
          // hinter der Karte. Der Wrapper unten muss zusätzlich positioniert sein.
          position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0, zIndex: 1000,
          background: '#fff', border: '1px solid #E5E1E1', borderRadius: compact ? 10 : 14,
          boxShadow: '0 16px 38px rgba(38,38,38,0.16)', maxHeight: 240, overflowY: 'auto',
        }}>
          {plz.kind === 'incomplete' && (
            <div style={{ padding: '10px 12px', fontFamily: body, fontSize: 14, color: '#9A9494' }}>
              Noch {plz.missing} {plz.missing === 1 ? 'Ziffer' : 'Ziffern'} bis zur vollständigen PLZ
            </div>
          )}
          {plz.kind === 'loading' && (
            <div style={{ padding: '10px 12px', fontFamily: body, fontSize: 14, color: '#9A9494' }}>PLZ wird gesucht…</div>
          )}
          {plz.kind === 'miss' && (
            <div style={{ padding: '10px 12px', fontFamily: body, fontSize: 14, color: '#9A9494' }}>PLZ nicht gefunden</div>
          )}
          {plz.kind === 'hit' && (
            <button
              type="button"
              onClick={() => pickPlz(plz)}
              style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left', padding: '9px 12px', background: 'none', border: 'none', cursor: 'pointer' }}
            >
              <PinIcon size={14} />
              <span style={{ fontFamily: body, fontWeight: 600, fontSize: 15, color: '#2C2C2C' }}>PLZ {plz.plz}</span>
            </button>
          )}
          {suggestions.map(p => (
            <button
              key={`${p.name}-${p.state}-${p.lat}`}
              type="button"
              onClick={() => pickPlace(p)}
              style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left', padding: '9px 12px', background: 'none', border: 'none', cursor: 'pointer' }}
            >
              <PinIcon size={14} />
              <span style={{ fontFamily: body, fontWeight: 600, fontSize: 15, color: '#2C2C2C' }}>{p.name}</span>
              {p.state && <span style={{ fontFamily: body, fontSize: 12, color: '#9A9494' }}>{p.state}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  /**
   * Standort-Knopf samt Rückmeldung. Der Hinweis sitzt direkt über dem Knopf, damit klar
   * ist, worauf er sich bezieht; `role="status"` liest ihn Screenreadern vor, ohne den
   * Fokus zu stehlen.
   */
  const locateButton = (
    <>
      {locateError && (
        <div
          role="status"
          style={{
            position: 'absolute', right: 14, bottom: 64, zIndex: 500, maxWidth: 236,
            background: '#2C2C2C', color: '#fff', fontFamily: body, fontSize: 12.5, lineHeight: 1.35,
            padding: '9px 12px', borderRadius: 10, boxShadow: '0 8px 22px rgba(38,38,38,0.28)',
          }}
        >
          {locateError}
        </div>
      )}
      <button
        type="button" onClick={useOwnLocation} disabled={locating}
        aria-label="Eigenen Standort verwenden"
        aria-busy={locating}
        style={{
          position: 'absolute', right: 14, bottom: 14, zIndex: 500, width: 42, height: 42, borderRadius: '50%',
          background: '#fff', border: 'none', cursor: locating ? 'default' : 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 6px 18px rgba(38,38,38,0.20)',
        }}
      >
        {locating ? (
          // Ein sich drehender Ring statt bloßer Transparenz: der Antippende muss sehen,
          // dass etwas läuft — die Ortung darf bis zu 20 s dauern.
          <span
            style={{
              width: 17, height: 17, borderRadius: '50%', boxSizing: 'border-box',
              border: `2.2px solid ${BERRY}33`, borderTopColor: BERRY,
              animation: 'rad-spin 0.8s linear infinite',
            }}
          />
        ) : (
          <svg width={19} height={19} viewBox="0 0 24 24" fill="none" stroke={BERRY} strokeWidth={2.2} strokeLinecap="round">
            <circle cx="12" cy="12" r="7" /><circle cx="12" cy="12" r="1.6" fill={BERRY} />
            <path d="M12 2v3M12 19v3M2 12h3M19 12h3" />
          </svg>
        )}
      </button>
    </>
  );

  const map = (
    <LocationRadiusMap
      center={center} radiusKm={radius} points={visiblePoints} radiusActive={radiusOn}
      favoriteIds={favoriteIds}
      permanentPoints={visiblePermanent}
      onPickCenter={pickCoord} centerLabel={name || undefined} compact={fullscreen}
      insetTop={fullscreen ? mapInsetTop : 0}
    />
  );

  if (fullscreen) {
    return (
      <div style={{ position: 'fixed', inset: 0, zIndex: 200, background: '#fff', display: 'flex', flexDirection: 'column' }}>
        {/* Raster statt space-between: mit „Abbrechen" links und dem kürzeren „Fertig" rechts
            säße der Titel bei space-between sichtbar neben der Mitte. Gleich breite Außenspalten
            zentrieren ihn echt. */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', gap: 12, padding: 'calc(14px + env(safe-area-inset-top,0px)) 16px 11px', borderBottom: '1px solid #EDE9E9' }}>
          <button type="button" onClick={onClose} style={{ justifySelf: 'start', background: 'none', border: 'none', cursor: 'pointer', fontFamily: body, fontSize: 15, color: BERRY, padding: 0 }}>Abbrechen</button>
          <span style={{ fontFamily: "'Archivo',sans-serif", fontWeight: 800, fontSize: 16, color: '#2C2C2C' }}>Ort</span>
          <button type="button" onClick={apply} style={{ justifySelf: 'end', background: 'none', border: 'none', cursor: 'pointer', fontFamily: body, fontWeight: 700, fontSize: 15, color: BERRY, padding: 0 }}>Fertig</button>
        </div>

        <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
          {map}
          {/* Suchfeld und Umkreis schweben über der Karte statt in einem weißen Band darüber —
              so reicht die Karte bis unter den Header und der Screen wirkt nicht zweigeteilt.
              Der hohe z-index muss sein: Leaflets Panes reichen bis 800. */}
          <div ref={floatingRef} style={{ position: 'absolute', left: 12, right: 12, top: FLOATING_TOP, zIndex: 1000, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {searchField(false, true)}
            <div style={{ background: '#fff', borderRadius: 14, padding: '12px 14px 10px', boxShadow: '0 6px 20px rgba(38,38,38,0.14)' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <span style={{ fontFamily: body, fontWeight: 600, fontSize: 14, color: '#4A4A4A' }}>Umkreis</span>
                {radiusValue}
              </div>
              {radiusSlider}
              <div style={{ marginTop: 8 }}>{hitLine(12, 8)}</div>
            </div>
            {/* Filter zugeklappt, solange sie nicht gebraucht werden — aufgeklappt verdeckten sie
                auf dem Phone die halbe Karte. Die Zeile sagt trotzdem, wonach gerade gefiltert wird. */}
            <div style={{ background: '#fff', borderRadius: 14, boxShadow: '0 6px 20px rgba(38,38,38,0.14)', overflow: 'hidden' }}>
              <button
                type="button" onClick={() => setFiltersOpen(o => !o)} aria-expanded={filtersOpen}
                style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, background: 'none', border: 'none', cursor: 'pointer', padding: '12px 14px', textAlign: 'left' }}
              >
                <span style={{ display: 'flex', alignItems: 'baseline', gap: 8, minWidth: 0 }}>
                  <span style={{ fontFamily: body, fontWeight: 600, fontSize: 14, color: '#4A4A4A', flexShrink: 0 }}>Filter</span>
                  <span style={{ fontFamily: body, fontSize: 12.5, color: filterCount || (permanentPoints && showPermanent) ? '#2C2C2C' : '#9A9494', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{summaryText}</span>
                </span>
                <span style={{ fontFamily: "'Space Mono',monospace", fontSize: 12, color: '#9A9494', flexShrink: 0 }}>{filtersOpen ? '▲' : '▾'}</span>
              </button>
              {/* Deckel plus Scrollen: die schwebende Spalte scrollt selbst nicht, auf einem
                  kurzen Display lägen Distanz-Chips und „Zurücksetzen" sonst unerreichbar
                  unter dem Kartenrand. */}
              {filtersOpen && (
                <div style={{ padding: '0 14px 14px', maxHeight: '52vh', overflowY: 'auto' }}>{filterControls}</div>
              )}
            </div>
          </div>
          {locateButton}
        </div>

        <div style={{ padding: '13px 16px calc(16px + env(safe-area-inset-bottom,0px))', borderTop: '1px solid #EDE9E9' }}>
          <button type="button" onClick={apply}
            style={{ width: '100%', background: BERRY, color: '#fff', border: 'none', borderRadius: 14, padding: 16, fontFamily: body, fontWeight: 700, fontSize: 15, cursor: 'pointer' }}>
            {ctaLabel}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(38,38,38,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 32 }}
    >
      <div style={{ width: '100%', maxWidth: MODAL_MAX_WIDTH, background: '#fff', borderRadius: 18, boxShadow: '0 40px 90px rgba(0,0,0,0.35)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 22px', borderBottom: '1px solid #EDE9E9' }}>
          <span style={{ fontFamily: "'Archivo',sans-serif", fontWeight: 800, fontSize: 18, color: '#2C2C2C' }}>Ort &amp; Umkreis</span>
          <button type="button" onClick={onClose} aria-label="Schließen" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9A9494', fontSize: 18, lineHeight: 1, padding: 0 }}>✕</button>
        </div>
        {/* 300 px statt 270: bei drei Kategorie-Kacheln nebeneinander bricht „Rennrad" sonst um.
            Der Rest gehört der Karte — sie wächst mit dem Fenster, die Spalte bleibt schmal. */}
        <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr' }}>
          {/* position/zIndex wie im Vollbild: die Vorschlagsliste muss über der Karte liegen. */}
          <div style={{ padding: 20, borderRight: '1px solid #EDE9E9', display: 'flex', flexDirection: 'column', gap: 14, minHeight: MODAL_BODY_HEIGHT, boxSizing: 'border-box', position: 'relative', zIndex: 1000 }}>
            <div>
              <div style={{ ...mono, fontSize: 10, marginBottom: 8 }}>Ort</div>
              {searchField(true)}
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <span style={{ ...mono, fontSize: 10 }}>Umkreis</span>
                {radiusValue}
              </div>
              {radiusSlider}
            </div>
            {filterControls}
            {hitLine(13.5, 9)}
            <button type="button" onClick={apply}
              style={{ marginTop: 'auto', background: BERRY, color: '#fff', border: 'none', borderRadius: 11, padding: 14, fontFamily: body, fontWeight: 700, fontSize: 14.5, cursor: 'pointer' }}>
              {ctaLabel}
            </button>
          </div>
          {/* minHeight statt height: wächst die Seitenspalte (lange Ortsnamen, kleines Fenster),
              zieht die Zeile mit und die Karte füllt sie weiter aus. */}
          <div style={{ position: 'relative', minHeight: MODAL_BODY_HEIGHT }}>
            {map}
            {locateButton}
          </div>
        </div>
      </div>
    </div>
  );
}
